import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Save, Loader2 } from 'lucide-react';
import apiService from '../services/apiService';
import { useAuth } from '../contexts/AuthContext';

export default function Margens() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const admin = isAdmin();

  const [linhas, setLinhas] = useState([]);
  const [edicoes, setEdicoes] = useState({});
  const [salvandoOrdem, setSalvandoOrdem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [sucessoOrdem, setSucessoOrdem] = useState(null);

  const carregar = useCallback(async () => {
    setLoading(true);
    setErro('');
    try {
      const resp = await apiService.configMarkup.listar();
      setLinhas(resp.data.data ?? []);
    } catch {
      setErro('Erro ao carregar configuração de markup. Verifique a conexão com o servidor.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  function valorAtual(linha, campo) {
    const edicao = edicoes[linha.ordem_tabela];
    if (edicao && edicao[campo] !== undefined) return edicao[campo];
    return linha[campo];
  }

  function handleChange(ordemTabela, campo, valor) {
    setEdicoes(prev => ({
      ...prev,
      [ordemTabela]: { ...prev[ordemTabela], [campo]: valor },
    }));
  }

  async function handleSalvar(linha) {
    const markup_moveis = Number(valorAtual(linha, 'markup_moveis'));
    const markup_eletro = Number(valorAtual(linha, 'markup_eletro'));

    setSalvandoOrdem(linha.ordem_tabela);
    setErro('');
    try {
      const resp = await apiService.configMarkup.atualizar(linha.ordem_tabela, { markup_moveis, markup_eletro });
      setLinhas(resp.data.data ?? []);
      setEdicoes(prev => {
        const { [linha.ordem_tabela]: _omitido, ...resto } = prev;
        return resto;
      });
      setSucessoOrdem(linha.ordem_tabela);
      setTimeout(() => setSucessoOrdem(null), 2000);
    } catch (e) {
      setErro(e.response?.data?.error || 'Erro ao salvar markup.');
    } finally {
      setSalvandoOrdem(null);
    }
  }

  function temAlteracao(linha) {
    const edicao = edicoes[linha.ordem_tabela];
    if (!edicao) return false;
    return Object.keys(edicao).some(campo => Number(edicao[campo]) !== Number(linha[campo]));
  }

  return (
    <div className="p-6 max-w-4xl mx-auto">

      {/* Cabeçalho */}
      <div className="flex items-center gap-3 mb-6">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 border border-gray-200 rounded-lg px-3 py-1.5 hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft size={15} />Voltar
        </button>
        <div>
          <h1 className="text-xl font-medium text-gray-900 leading-tight">Margens</h1>
          <p className="text-sm text-gray-500">
            Markup mínimo por tabela de preço, para móveis e eletrodomésticos
            {!admin && ' (somente leitura)'}
          </p>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

        {loading && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">Carregando...</div>
        )}

        {erro && (
          <div className="px-5 py-4 text-sm text-red-600 border-b border-gray-100">{erro}</div>
        )}

        {!loading && linhas.length === 0 && !erro && (
          <div className="px-5 py-12 text-center text-sm text-gray-400">
            Nenhuma tabela configurada.
          </div>
        )}

        {!loading && linhas.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-xs text-gray-500 border-b border-gray-100">
                  <th className="text-left px-5 py-3 font-medium">Tabela de preço</th>
                  <th className="text-left px-4 py-3 font-medium">Markup mínimo Móveis (%)</th>
                  <th className="text-left px-4 py-3 font-medium">Markup mínimo Eletro (%)</th>
                  {admin && <th className="text-left px-4 py-3 font-medium">Ações</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {linhas.map(linha => (
                  <tr key={linha.ordem_tabela} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3">
                      <div className="font-medium text-gray-900">{linha.nome_tabela}</div>
                      <div className="text-xs text-gray-400">Tabela {linha.ordem_tabela}</div>
                    </td>

                    <td className="px-4 py-3">
                      {admin ? (
                        <input
                          type="number"
                          min="0"
                          max="500"
                          step="0.1"
                          className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                          value={valorAtual(linha, 'markup_moveis')}
                          onChange={e => handleChange(linha.ordem_tabela, 'markup_moveis', e.target.value)}
                        />
                      ) : (
                        <span className="text-gray-900">{linha.markup_moveis}%</span>
                      )}
                    </td>

                    <td className="px-4 py-3">
                      {admin ? (
                        <input
                          type="number"
                          min="0"
                          max="500"
                          step="0.1"
                          className="w-24 border border-gray-200 rounded-lg px-2 py-1.5 text-sm focus:outline-none focus:border-blue-400"
                          value={valorAtual(linha, 'markup_eletro')}
                          onChange={e => handleChange(linha.ordem_tabela, 'markup_eletro', e.target.value)}
                        />
                      ) : (
                        <span className="text-gray-900">{linha.markup_eletro}%</span>
                      )}
                    </td>

                    {admin && (
                      <td className="px-4 py-3">
                        <button
                          onClick={() => handleSalvar(linha)}
                          disabled={!temAlteracao(linha) || salvandoOrdem === linha.ordem_tabela}
                          className="flex items-center gap-1.5 px-3 py-1.5 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg text-sm hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed"
                        >
                          {salvandoOrdem === linha.ordem_tabela
                            ? <Loader2 size={13} className="animate-spin" />
                            : <Save size={13} />}
                          {sucessoOrdem === linha.ordem_tabela ? 'Salvo!' : 'Salvar'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
